import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronLeft, MapPin, Star, Trophy, Target, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useLang } from "@/lib/lang";
import { useAuth, API } from "@/lib/auth";
import { CATEGORY_COLORS, CATEGORY_ICONS } from "@/lib/constants";
import AdminDashboard from "@/components/AdminDashboard";

export default function Profile() {
  const { t, lang } = useLang();
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/profile`, { withCredentials: true }).then((r) => setData(r.data));
    axios.get(`${API}/categories`).then((r) => setCategories(r.data));
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <Compass size={48} className="mx-auto text-[#8b6f3d] mb-4" />
          <h1 className="font-serif-mystery text-3xl mb-3 text-zinc-900">{t("profile")}</h1>
          <p className="text-zinc-600 mb-6">{t("loginToComment")}</p>
          <Button onClick={login} className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white" data-testid="profile-login-btn">
            {t("signInGoogle")}
          </Button>
          <div className="mt-6">
            <Link to="/map" className="text-[#8b6f3d] hover:underline text-sm">← {t("backHome")}</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-zinc-400 font-mono-data text-sm">Loading…</div>;
  }

  const catMap = Object.fromEntries(categories.map((c) => [c.key, c]));

  return (
    <div className="min-h-screen bg-white text-zinc-900 pb-20">
      <div className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/map">
            <Button variant="outline" size="sm" data-testid="profile-back-btn" className="border-zinc-300">
              <ChevronLeft size={16} className="mr-1" /> {t("backHome")}
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-zinc-200" />}
            <div className="text-right">
              <div className="text-sm font-medium text-zinc-900" data-testid="profile-user-name">{user.name}</div>
              <div className="font-mono-data text-[10px] text-zinc-500">{user.email}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-10">
        <div className="mb-2 flex items-center gap-2 text-[#8b6f3d]">
          <div className="h-px w-8 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[10px] uppercase tracking-[0.3em]">{t("adventurerMode")}</span>
        </div>
        <h1 className="font-serif-mystery text-4xl md:text-5xl text-zinc-900 mb-8" data-testid="profile-title">
          {t("profile")}
        </h1>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="border border-zinc-200 rounded-2xl p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("totalVisits")}</div>
            <div className="font-serif-mystery text-4xl text-[#8b6f3d]" data-testid="stat-total-visits">{data.total_visits}</div>
            <div className="text-xs text-zinc-500 mt-1">{t("of")} {data.total_locations}</div>
          </div>
          <div className="border border-zinc-200 rounded-2xl p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("progression")}</div>
            <div className="font-serif-mystery text-4xl text-[#8b6f3d]" data-testid="stat-progression">{data.progression_pct}%</div>
            <Progress value={data.progression_pct} className="mt-2 h-1.5" />
          </div>
          <div className="border border-zinc-200 rounded-2xl p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("badges")}</div>
            <div className="font-serif-mystery text-4xl text-[#8b6f3d]" data-testid="stat-badges">{data.badges.length}</div>
            <div className="text-xs text-zinc-500 mt-1">/ 5</div>
          </div>
          <div className="border border-zinc-200 rounded-2xl p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("categoriesExplored")}</div>
            <div className="font-serif-mystery text-4xl text-[#8b6f3d]" data-testid="stat-categories">{Object.keys(data.categories).length}</div>
            <div className="text-xs text-zinc-500 mt-1">/ 9</div>
          </div>
        </div>

        {/* Achievements (Adventurer Mode badges) */}
        <section className="mb-10">
          <h2 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-2 flex items-center gap-2">
            <span>🏕️</span> {lang === "fr" ? "Badges d'Aventurier" : "Adventurer Achievements"}
          </h2>
          <p className="text-sm text-zinc-500 mb-4">
            {lang === "fr" ? "Débloqués selon vos catégories visitées et pays explorés." : "Unlocked based on categories visited and countries explored."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(data.achievements || []).map((a) => (
              <div
                key={a.key}
                data-testid={`achievement-${a.key}`}
                className={`border rounded-2xl p-5 transition-all ${
                  a.earned
                    ? "border-[#8b6f3d] bg-gradient-to-br from-amber-50 to-white shadow-sm"
                    : "border-zinc-200 opacity-70"
                }`}
              >
                <div className="text-4xl mb-2">{a.icon}</div>
                <div className="font-medium text-zinc-900">{a[lang]}</div>
                <div className="text-[11px] text-zinc-500 font-mono-data mt-1 leading-snug">{a[`${lang}_desc`]}</div>
                <div className="mt-2">
                  <Progress value={(a.progress / a.target) * 100} className="h-1" />
                  <div className="font-mono-data text-[10px] text-zinc-500 mt-1">{a.progress}/{a.target}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Badges (visit tiers) */}
        <section className="mb-10">
          <h2 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-4 flex items-center gap-2"><Trophy size={20} /> {t("badges")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[10, 30, 60, 100, 200].map((threshold, i) => {
              const badgeMeta = [
                { k: "beginner", icon: "🥉" },
                { k: "adventurer", icon: "🥈" },
                { k: "hunter", icon: "🥇" },
                { k: "master", icon: "💎" },
                { k: "legend", icon: "👑" },
              ][i];
              const earned = data.badges.some((b) => b.key === badgeMeta.k);
              const labelFr = ["Explorateur débutant", "Aventurier", "Chasseur de mystères", "Maître explorateur", "Légende des mystères"][i];
              const labelEn = ["Beginner Explorer", "Adventurer", "Mystery Hunter", "Master Explorer", "Legend of Mysteries"][i];
              return (
                <div
                  key={badgeMeta.k}
                  data-testid={`badge-${badgeMeta.k}`}
                  className={`border rounded-2xl p-5 ${earned ? "border-[#8b6f3d] bg-amber-50/50" : "border-zinc-200 opacity-60"}`}
                >
                  <div className="text-4xl mb-2">{badgeMeta.icon}</div>
                  <div className="font-medium text-zinc-900">{lang === "fr" ? labelFr : labelEn}</div>
                  <div className="font-mono-data text-[10px] text-zinc-500 mt-1">
                    {Math.min(data.total_visits, threshold)}/{threshold} {lang === "fr" ? "lieux" : "places"}
                  </div>
                  {!earned && (
                    <Progress value={(data.total_visits / threshold) * 100} className="mt-2 h-1" />
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Categories */}
        <section className="mb-10">
          <h2 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-4 flex items-center gap-2"><Target size={20} /> {t("categoriesExplored")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {categories.map((c) => {
              const count = data.categories[c.key] || 0;
              return (
                <div key={c.key} className="border border-zinc-200 rounded-xl p-4 flex items-center gap-3" data-testid={`cat-stat-${c.key}`}>
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm text-white shrink-0"
                    style={{ background: c.color }}
                  >
                    {CATEGORY_ICONS[c.key]}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-900 truncate">{c[lang]}</div>
                    <div className="font-mono-data text-[11px] text-zinc-500">{count} {lang === "fr" ? "visite(s)" : "visit(s)"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Recent visits */}
        <section>
          <h2 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-4 flex items-center gap-2"><Star size={20} /> {t("recentVisits")}</h2>
          {data.recent_visits.length === 0 ? (
            <p className="text-zinc-500 italic" data-testid="no-visits-text">{t("noVisits")}</p>
          ) : (
            <div className="space-y-2">
              {data.recent_visits.map((v) => (
                <div key={v.location_id} className="border border-zinc-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`recent-${v.location_id}`}>
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white shrink-0"
                    style={{ background: (catMap[v.category] || {}).color || "#8b6f3d" }}
                  >
                    {CATEGORY_ICONS[v.category]}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-900 truncate">{v.name?.[lang] || v.name?.fr}</div>
                    <div className="font-mono-data text-[10px] text-zinc-500 flex items-center gap-2">
                      <MapPin size={10} /> {v.country}
                      <span className="ml-auto">{new Date(v.created_at).toLocaleDateString(lang)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Admin dashboard — visible only to admin user */}
        {data?.user?.is_admin && <AdminDashboard />}
      </div>
    </div>
  );
}
